import React, { useState } from "react";

import Select from "../../../../components/input/select/Select";
import TextField from "../../../../components/input/textfield/TextField";
import TextArea from "../../../../components/input/textarea/TextArea";

import {
  FormSection,
  FormRow,
} from "../../../../components/layout/forms/Index";

import TalentModal from "./TalentModal";
import { clean, getApiError } from "../utils/talentUtils";

import "./TalentForms.css";

const asDate = (value) => (value ? String(value).slice(0, 10) : "");

const initialFields = (initial) => ({
  talentSportId: initial?.talentSportId ?? "",
  title: initial?.title || "",
  organisation: initial?.organisation || "",
  achievementType: initial?.achievementType || "",
  achievedOn: asDate(initial?.achievedOn),
  description: initial?.description || "",
});

export default function TalentAchievementForm({
  initial,
  sports = [],
  saving = false,
  onSave,
  onClose,
}) {
  const [fields, setFields] = useState(() => initialFields(initial));
  const [errors, setErrors] = useState({});
  const [error, setError] = useState("");

  const sportOptions = [
    { label: "General / Not specified", value: "" },
    ...sports.map((sport) => ({
      label: sport.sport,
      value: String(sport.sportId),
    })),
  ];

  const change = ({ target }) => {
    const { name, value } = target;

    setFields((previous) => ({
      ...previous,
      [name]: value,
    }));

    setErrors((previous) => ({
      ...previous,
      [name]: "",
    }));

    setError("");
  };

  const save = async () => {
    if (saving) return;

    const next = {};
    const title = clean(fields.title);

    if (!title) {
      next.title = "Achievement title is required.";
    }

    if (
      fields.talentSportId !== "" &&
      !sports.some(
        (sport) => String(sport.sportId) === String(fields.talentSportId),
      )
    ) {
      next.talentSportId = "Select a valid sport.";
    }

    setErrors(next);

    if (Object.keys(next).length) return;

    const payload = {
      talentSportId:
        fields.talentSportId === "" ? null : Number(fields.talentSportId),
      title,
      organisation: clean(fields.organisation) || null,
      achievementType: clean(fields.achievementType) || null,
      achievedOn: fields.achievedOn || null,
      description: clean(fields.description) || null,
    };

    try {
      setError("");
      await onSave(payload);
    } catch (err) {
      setError(getApiError(err));
    }
  };

  return (
    <TalentModal
      title={initial ? "Edit Achievement" : "Add Achievement"}
      description={
        initial
          ? "Update your award, selection or sporting milestone."
          : "Record awards, selections and sporting milestones."
      }
      busy={saving}
      onClose={onClose}
      onSave={save}
    >
      {error && (
        <p className="user-talent__error" role="alert">
          {error}
        </p>
      )}

      <FormSection>
        <Select
          label="Sport"
          name="talentSportId"
          value={String(fields.talentSportId)}
          onChange={change}
          items={sportOptions}
          error={errors.talentSportId}
        />

        <TextField
          label="Achievement Title"
          name="title"
          value={fields.title}
          onChange={change}
          placeholder="e.g. Best and Fairest"
          maxLength={150}
          required
        />

        <TextField
          label="Organisation"
          name="organisation"
          value={fields.organisation}
          onChange={change}
          placeholder="School, club or association"
          maxLength={150}
        />

        <FormRow>
          <TextField
            label="Achievement Type"
            name="achievementType"
            value={fields.achievementType}
            onChange={change}
            placeholder="e.g. Award, Selection"
            maxLength={60}
          />

          <TextField
            label="Achievement Date"
            name="achievedOn"
            type="date"
            value={fields.achievedOn}
            onChange={change}
          />
        </FormRow>

        <TextArea
          label="Description"
          name="description"
          value={fields.description}
          onChange={change}
          placeholder="Describe your achievement..."
          maxLength={10000}
          maxHeight={140}
        />
      </FormSection>
    </TalentModal>
  );
}
